import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, TrendingUp, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import { useAlert } from './AlertProvider';

interface Tier {
    id?: number;
    tier_number: number;
    min_quantity: number;
    max_quantity?: number;
    discount_percentage?: number;
    free_units?: number;
}

interface TierConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    productName: string;
    basePrice: number;
    mode: 'percent' | 'free_units';
    existingTiers?: Tier[];
    onSave: (tiers: Tier[], selectedMode: 'percent' | 'free_units') => void;
    readOnly?: boolean;
}

const TierConfigModal: React.FC<TierConfigModalProps> = ({
    isOpen,
    onClose,
    productName,
    basePrice,
    mode,
    existingTiers = [],
    onSave,
    readOnly = false
}) => {
    const { showSuccess, showError } = useAlert();
    const [selectedMode, setSelectedMode] = useState<'percent' | 'free_units'>(mode);
    const [error, setError] = useState<string>('');
    const [tiers, setTiers] = useState<Tier[]>(() => {
        // Filter existing tiers based on current mode
        const filteredTiers = existingTiers.filter(tier => {
            if (mode === 'percent') {
                return tier.discount_percentage !== undefined && tier.discount_percentage !== null;
            } else {
                return tier.free_units !== undefined && tier.free_units !== null;
            }
        });

        // If no matching tiers, create a default one
        if (filteredTiers.length === 0) {
            return [{
                tier_number: 1,
                min_quantity: 0,
                discount_percentage: mode === 'percent' ? 0 : undefined,
                free_units: mode === 'free_units' ? 0 : undefined
            }];
        }

        return filteredTiers;
    });

    useEffect(() => {
        // Filter existing tiers based on selected mode when they change
        const filteredTiers = existingTiers.filter(tier => {
            if (selectedMode === 'percent') {
                return tier.discount_percentage !== undefined && tier.discount_percentage !== null;
            } else {
                return tier.free_units !== undefined && tier.free_units !== null;
            }
        });

        if (filteredTiers.length > 0) {
            setTiers(filteredTiers);
        } else {
            // Reset to default tier if no matching tiers
            setTiers([{
                tier_number: 1,
                min_quantity: 0,
                discount_percentage: selectedMode === 'percent' ? 0 : undefined,
                free_units: selectedMode === 'free_units' ? 0 : undefined
            }]);
        }
    }, [existingTiers, selectedMode]);

    const addTier = () => {
        const lastTier: Tier | undefined = tiers[tiers.length - 1];
        const newMinQty: number = (lastTier?.min_quantity ?? 0) + 1;

        setTiers([...tiers, {
            tier_number: tiers.length + 1,
            min_quantity: newMinQty,
            discount_percentage: selectedMode === 'percent' ? 0 : undefined,
            free_units: selectedMode === 'free_units' ? 0 : undefined
        }]);
    };

    const removeTier = async (index: number) => {
        const tierToDelete = tiers[index];

        // If tier has an ID, delete from backend immediately
        if (tierToDelete.id) {
            try {
                await api.delete(`/client-pricing/item-tier/${tierToDelete.id}`);
                showSuccess('Palier supprimé');
            } catch (error: any) {
                showError(error.response?.data?.detail || 'Erreur lors de la suppression');
                return;
            }
        }

        // Remove from local state
        const newTiers = tiers.filter((_, i) => i !== index);
        // Renumber tiers
        setTiers(newTiers.map((tier, i) => ({ ...tier, tier_number: i + 1 })));
    };

    const updateTier = (index: number, field: keyof Tier, value: any) => {
        const newTiers = [...tiers];
        newTiers[index] = { ...newTiers[index], [field]: value };
        setTiers(newTiers);
    };

    const handleSave = () => {
        setError('');

        // Validate tiers based on selected mode
        const validTiers = tiers.filter(tier => {
            if (selectedMode === 'percent') {
                return tier.discount_percentage !== undefined && tier.discount_percentage >= 0;
            } else {
                return tier.free_units !== undefined && tier.free_units >= 0;
            }
        });

        if (validTiers.length === 0) {
            setError('Veuillez configurer au moins un palier valide');
            return;
        }

        const sanitizedTiers: Tier[] = validTiers.map((tier) => ({
            ...tier,
            max_quantity: undefined
        }));

        onSave(sanitizedTiers, selectedMode);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                            <TrendingUp className="h-6 w-6 text-blue-600" />
                            Configuration des Paliers
                        </h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            {productName} • Prix de base: {basePrice.toFixed(2)} €
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-slate-500 dark:text-slate-500">Mode:</span>
                            <div className="flex bg-slate-200 dark:bg-slate-700 rounded-lg p-0.5">
                                <button
                                    type="button"
                                    onClick={() => !readOnly && setSelectedMode('percent')}
                                    className={`px-3 py-1 text-xs font-medium rounded transition-all ${selectedMode === 'percent'
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                        } ${readOnly ? 'cursor-not-allowed opacity-70' : ''}`}
                                    disabled={readOnly}
                                >
                                    % Pourcentage
                                </button>
                                <button
                                    type="button"
                                    onClick={() => !readOnly && setSelectedMode('free_units')}
                                    className={`px-3 py-1 text-xs font-medium rounded transition-all ${selectedMode === 'free_units'
                                        ? 'bg-purple-600 text-white shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                        } ${readOnly ? 'cursor-not-allowed opacity-70' : ''}`}
                                    disabled={readOnly}
                                >
                                    UG Unités Gratuites
                                </button>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {/* Tiers Table */}
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-slate-50 dark:bg-slate-800">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Palier</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Qté Min (lots)</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                                        {selectedMode === 'percent' ? 'Remise %' : 'UG (Gratuit)'}
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase w-20">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {tiers.map((tier, index) => (
                                    <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full text-sm font-semibold">
                                                Palier {tier.tier_number}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                value={tier.min_quantity}
                                                onChange={(e) => updateTier(index, 'min_quantity', parseInt(e.target.value) || 0)}
                                                className="w-20 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                                min="0"
                                                disabled={readOnly}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            {selectedMode === 'percent' ? (
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        value={tier.discount_percentage || ''}
                                                        onChange={(e) => updateTier(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                                                        className="w-20 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        min="0"
                                                        max="100"
                                                        step="0.1"
                                                        disabled={readOnly}
                                                    />
                                                    <span className="text-sm text-slate-600 dark:text-slate-400">%</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        value={tier.free_units || ''}
                                                        onChange={(e) => updateTier(index, 'free_units', parseInt(e.target.value) || 0)}
                                                        className="w-20 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        min="0"
                                                        disabled={readOnly}
                                                    />
                                                    <span className="text-sm text-slate-600 dark:text-slate-400">UG</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button
                                                onClick={() => removeTier(index)}
                                                className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                                title="Supprimer ce palier"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Add Tier Button */}
                    {!readOnly && (
                        <button
                            onClick={addTier}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
                        >
                            <Plus className="h-5 w-5" />
                            Ajouter un palier
                        </button>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500 p-4 rounded">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                                <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                                    {error}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Warning */}
                    <div className="bg-orange-50 dark:bg-orange-950/20 border-l-4 border-orange-500 p-4 rounded">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-orange-700 dark:text-orange-400">
                                    Conditions de paiement automatiques
                                </p>
                                <p className="text-sm text-orange-600 dark:text-orange-500 mt-1">
                                    L'application d'un palier induit de facto un mode de paiement par Prélèvement à 30 jours fin de mois.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Preview */}
                    {tiers.length > 0 && (
                        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Aperçu des prix</h3>
                            <div className="space-y-2">
                                {tiers.map((tier, index) => {
                                    const calculatedPrice = mode === 'percent'
                                        ? basePrice * (1 - (tier.discount_percentage || 0) / 100)
                                        : basePrice; // For UG mode, base price stays same but customer gets free units

                                    return (
                                        <div key={index} className="flex items-center justify-between text-sm">
                                            <span className="text-slate-600 dark:text-slate-400">
                                                Palier {tier.tier_number}: ≥ {tier.min_quantity} lots
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {mode === 'percent' ? (
                                                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                                        {calculatedPrice.toFixed(2)} € ({tier.discount_percentage}% de remise)
                                                    </span>
                                                ) : (
                                                    <span className="font-semibold text-purple-600 dark:text-purple-400">
                                                        {basePrice.toFixed(2)} € + {tier.free_units} gratuit
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-slate-200 dark:border-slate-700 sticky bottom-0 bg-white dark:bg-slate-900">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                        Annuler
                    </button>
                    {readOnly ? (
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors font-medium"
                        >
                            Fermer
                        </button>
                    ) : (
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            Enregistrer les paliers
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TierConfigModal;
